import React, { useEffect, useMemo, useState } from "react";
import { FaEnvelope, FaSave, FaCheckCircle, FaExclamationTriangle } from "react-icons/fa";
import api from "../api";

const initialState = {
  smtpHost: "",
  smtpPort: "",
  useTLS: false,
  senderName: "",
  senderEmail: "",
  authUser: "",
};

export default function MailSettings() {
  const [form, setForm] = useState(initialState);
  const [originalForm, setOriginalForm] = useState(initialState);
  const [hasAuthPassword, setHasAuthPassword] = useState(false);
  const [originalHasPassword, setOriginalHasPassword] = useState(false);
  const [authPassword, setAuthPassword] = useState("");
  const [authPasswordChanged, setAuthPasswordChanged] = useState(false);
  const [clearPassword, setClearPassword] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [testRecipient, setTestRecipient] = useState("");

  useEffect(() => {
    let mounted = true;
    const loadConfiguration = async () => {
      setLoading(true);
      setError("");
      try {
        const response = await api.get("/mail/configuration");
        if (!mounted) return;
        const data = response.data || {};
        setForm({
          smtpHost: data.smtpHost || "",
          smtpPort:
            data.smtpPort === null || data.smtpPort === undefined
              ? ""
              : String(data.smtpPort),
          useTLS: Boolean(data.useTLS),
          senderName: data.senderName || "",
          senderEmail: data.senderEmail || "",
          authUser: data.authUser || "",
        });
        setOriginalForm({
          smtpHost: data.smtpHost || "",
          smtpPort:
            data.smtpPort === null || data.smtpPort === undefined
              ? ""
              : String(data.smtpPort),
          useTLS: Boolean(data.useTLS),
          senderName: data.senderName || "",
          senderEmail: data.senderEmail || "",
          authUser: data.authUser || "",
        });
        setHasAuthPassword(Boolean(data.hasAuthPassword));
        setOriginalHasPassword(Boolean(data.hasAuthPassword));
        setAuthPassword("");
        setAuthPasswordChanged(false);
        setClearPassword(false);
        setSuccess("");
      } catch (err) {
        if (!mounted) return;
        setError(err.response?.data?.error || "Não foi possível carregar a configuração atual.");
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    loadConfiguration();
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (success) {
      const timeout = setTimeout(() => setSuccess(""), 4000);
      return () => clearTimeout(timeout);
    }
    return undefined;
  }, [success]);

  const hasChanges = useMemo(() => {
    const baseChanged = Object.entries(form).some(([key, value]) => {
      const originalValue = originalForm[key];
      if (key === "useTLS") {
        return Boolean(value) !== Boolean(originalValue);
      }
      return String(value ?? "") !== String(originalValue ?? "");
    });
    const passwordChanged =
      (clearPassword && originalHasPassword) || authPasswordChanged;
    return baseChanged || passwordChanged;
  }, [authPasswordChanged, clearPassword, form, originalForm, originalHasPassword]);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleToggleTls = (event) => {
    handleChange("useTLS", event.target.checked);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (saving) return;
    setSaving(true);
    setError("");
    setSuccess("");
    try {
      const payload = {
        smtpHost: form.smtpHost || null,
        smtpPort: form.smtpPort ? Number(form.smtpPort) : null,
        useTLS: Boolean(form.useTLS),
        senderName: form.senderName || null,
        senderEmail: form.senderEmail || null,
        authUser: form.authUser || null,
      };
      if (authPasswordChanged) {
        payload.authPassword = authPassword || null;
      }
      if (clearPassword) {
        payload.clearAuthPassword = true;
      }
      const response = await api.put("/mail/configuration", payload);
      const data = response.data || {};
      setForm({
        smtpHost: data.smtpHost || "",
        smtpPort:
          data.smtpPort === null || data.smtpPort === undefined
            ? ""
            : String(data.smtpPort),
        useTLS: Boolean(data.useTLS),
        senderName: data.senderName || "",
        senderEmail: data.senderEmail || "",
        authUser: data.authUser || "",
      });
      setOriginalForm({
        smtpHost: data.smtpHost || "",
        smtpPort:
          data.smtpPort === null || data.smtpPort === undefined
            ? ""
            : String(data.smtpPort),
        useTLS: Boolean(data.useTLS),
        senderName: data.senderName || "",
        senderEmail: data.senderEmail || "",
        authUser: data.authUser || "",
      });
      setHasAuthPassword(Boolean(data.hasAuthPassword));
      setOriginalHasPassword(Boolean(data.hasAuthPassword));
      setAuthPassword("");
      setAuthPasswordChanged(false);
      setClearPassword(false);
      setSuccess("Configuração salva com sucesso.");
    } catch (err) {
      setError(err.response?.data?.error || "Falha ao salvar configuração.");
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    if (testing) return;
    setTesting(true);
    setError("");
    setSuccess("");
    try {
      await api.post("/mail/test", { to: testRecipient || undefined });
      setSuccess("E-mail de teste enviado com sucesso.");
    } catch (err) {
      setError(err.response?.data?.error || "Não foi possível enviar o e-mail de teste.");
    } finally {
      setTesting(false);
    }
  };

  const canSubmit = useMemo(() => {
    if (!hasChanges) return false;
    const requiredFilled = form.smtpHost && form.smtpPort && form.senderEmail;
    return Boolean(requiredFilled);
  }, [form.senderEmail, form.smtpHost, form.smtpPort, hasChanges]);

  return (
    <div className="space-y-6">
      <header className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-100 text-blue-600">
          <FaEnvelope />
        </div>
        <div>
          <h1 className="text-2xl font-semibold">Configuração de e-mail</h1>
          <p className="text-sm text-gray-600">
            Defina o servidor SMTP utilizado para enviar notificações automáticas da plataforma.
          </p>
        </div>
      </header>

      {error && (
        <div className="flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 p-4 text-red-700">
          <FaExclamationTriangle className="mt-1" />
          <div>
            <p className="font-medium">{error}</p>
            <p className="text-xs">Verifique os dados informados e tente novamente.</p>
          </div>
        </div>
      )}

      {success && (
        <div className="flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-emerald-700">
          <FaCheckCircle />
          <span>{success}</span>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        <section className="space-y-4 rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
          <h2 className="text-lg font-medium">Servidor SMTP</h2>
          <div className="grid gap-4 md:grid-cols-2">
            <label className="block text-sm font-medium text-gray-700">
              Host
              <input
                type="text"
                value={form.smtpHost}
                onChange={(e) => handleChange("smtpHost", e.target.value)}
                className="mt-1 w-full rounded border px-3 py-2"
                placeholder="smtp.seudominio.com"
                required
              />
            </label>
            <label className="block text-sm font-medium text-gray-700">
              Porta
              <input
                type="number"
                value={form.smtpPort}
                onChange={(e) => handleChange("smtpPort", e.target.value)}
                className="mt-1 w-full rounded border px-3 py-2"
                placeholder="587"
                required
              />
            </label>
          </div>
          <label className="flex items-center gap-3 text-sm font-medium text-gray-700">
            <input type="checkbox" checked={form.useTLS} onChange={handleToggleTls} />
            Utilizar TLS/SSL na conexão
          </label>
        </section>

        <section className="space-y-4 rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
          <h2 className="text-lg font-medium">Remetente</h2>
          <label className="block text-sm font-medium text-gray-700">
            Nome do remetente
            <input
              type="text"
              value={form.senderName}
              onChange={(e) => handleChange("senderName", e.target.value)}
              className="mt-1 w-full rounded border px-3 py-2"
              placeholder="Equipe BPM"
            />
          </label>
          <label className="block text-sm font-medium text-gray-700">
            E-mail do remetente
            <input
              type="email"
              value={form.senderEmail}
              onChange={(e) => handleChange("senderEmail", e.target.value)}
              className="mt-1 w-full rounded border px-3 py-2"
              placeholder="notificacoes@seudominio.com"
              required
            />
          </label>
        </section>

        <section className="space-y-4 rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
          <h2 className="text-lg font-medium">Autenticação</h2>
          <label className="block text-sm font-medium text-gray-700">
            Usuário
            <input
              type="text"
              value={form.authUser}
              onChange={(e) => handleChange("authUser", e.target.value)}
              className="mt-1 w-full rounded border px-3 py-2"
              placeholder="usuario@seudominio.com"
            />
          </label>
          <div className="grid gap-3 md:grid-cols-2">
            <label className="block text-sm font-medium text-gray-700">
              Senha
              <input
                type="password"
                value={authPassword}
                onChange={(e) => {
                  setAuthPassword(e.target.value);
                  setAuthPasswordChanged(true);
                  setClearPassword(false);
                }}
                className="mt-1 w-full rounded border px-3 py-2"
                placeholder={hasAuthPassword ? "••••••••" : "Informe a senha"}
              />
              {hasAuthPassword && !authPasswordChanged && !clearPassword && (
                <p className="mt-1 text-xs text-gray-500">
                  Uma senha já está configurada. Preencha o campo para substituí-la.
                </p>
              )}
            </label>
            <label className="mt-6 flex items-center gap-2 text-sm font-medium text-gray-700">
              <input
                type="checkbox"
                checked={clearPassword}
                onChange={(e) => {
                  setClearPassword(e.target.checked);
                  if (e.target.checked) {
                    setAuthPassword("");
                    setAuthPasswordChanged(false);
                  }
                }}
                disabled={!hasAuthPassword}
              />
              Remover senha cadastrada
            </label>
          </div>
        </section>

        <div className="flex flex-wrap items-center gap-3">
          <button
            type="submit"
            disabled={loading || saving || !canSubmit}
            className="flex items-center gap-2 rounded bg-blue-600 px-4 py-2 text-white transition disabled:opacity-50"
          >
            <FaSave />
            {saving ? "Salvando..." : "Salvar configuração"}
          </button>
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <input
              type="email"
              value={testRecipient}
              onChange={(e) => setTestRecipient(e.target.value)}
              className="w-64 rounded border px-3 py-2"
              placeholder="E-mail para teste (opcional)"
            />
            <button
              type="button"
              onClick={handleTest}
              disabled={testing}
              className="rounded border border-blue-600 px-3 py-2 text-blue-600 transition hover:bg-blue-50 disabled:opacity-50"
            >
              {testing ? "Enviando teste..." : "Enviar e-mail de teste"}
            </button>
          </div>
        </div>
      </form>

      {loading && (
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm text-gray-600">
          Carregando configuração atual...
        </div>
      )}

      <section className="rounded-lg border border-gray-200 bg-white p-4 text-sm text-gray-600">
        <h2 className="text-base font-semibold text-gray-800">Como funciona</h2>
        <ul className="mt-2 list-disc space-y-1 pl-5">
          <li>Os e-mails serão enviados para usuários que ativarem notificações em seu perfil.</li>
          <li>Preencha host, porta e remetente para habilitar o envio automático.</li>
          <li>Utilize o botão de teste para validar a configuração antes de salvar definitivamente.</li>
        </ul>
      </section>
    </div>
  );
}

